"use client";

import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Navigation, Clock, DollarSign, Target, Crosshair } from "lucide-react";
import { getViewModeChrome } from "../view-mode-chrome";

interface SetViewportLoadingProps {
  args?: any;
  viewMode?: string;
}

export function SetViewportLoading({ args, viewMode}: SetViewportLoadingProps) {
  const chrome = getViewModeChrome(viewMode);
  const [currentStage, setCurrentStage] = useState(0);

  const stages = useMemo(
    () => [
      { id: 1, text: "Setting time range...", duration: 1200, icon: Clock, iconColor: "text-muted-foreground", bgColor: "bg-muted/50" },
      { id: 2, text: "Adjusting price levels...", duration: 1200, icon: DollarSign, iconColor: "text-muted-foreground", bgColor: "bg-muted/60" },
      { id: 3, text: "Focusing viewport...", duration: 1200, icon: Target, iconColor: "text-muted-foreground", bgColor: "bg-muted/70" },
      { id: 4, text: "Centering view...", duration: 1200, icon: Crosshair, iconColor: "text-foreground", bgColor: "bg-muted/80" },
    ],
    []
  );

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    let totalDelay = 0;
    stages.forEach((stage, index) => {
      const t = setTimeout(() => setCurrentStage(index), totalDelay);
      totalDelay += stage.duration;
      timers.push(t);
    });
    return () => timers.forEach(clearTimeout);
  }, [stages]);

  const currentStageData = stages[currentStage] || stages[0];
  const Icon = currentStageData.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="w-full"
    >
      <div className={`rounded-md border ${chrome.card} overflow-hidden`}>
        {/* Progress bar */}
        <div className="absolute top-0 left-0 h-[1px] bg-gradient-to-r from-transparent via-border to-transparent">
          <motion.div
            className="h-full bg-gradient-to-r from-foreground/0 via-foreground/80 to-foreground/0"
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: 3, ease: "linear" }}
          />
        </div>

        <div className="px-3 py-2 flex items-center gap-3 min-w-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStage}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.3 }}
              className="flex items-center gap-2.5"
            >
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.6, 1, 0.6],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className={`w-7 h-7 rounded ${currentStageData.bgColor} flex items-center justify-center`}
              >
                <Icon
                  className={`w-4 h-4 ${currentStageData.iconColor}`}
                  strokeWidth={2}
                />
              </motion.div>
              <span className={`text-[13px] font-semibold ${chrome.text} truncate`}>
                {currentStageData.text}
              </span>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}
